import React from 'react';
import PropTypes from 'prop-types';
import { ProjectStatus, ProjectSynthesisJobType, ProjectSynthesisPartner } from '@Pages/RetroSynthesis/constants';
import cn from 'classnames';
import Button from '@Components/Button';
import { TooltipConstants } from '@Components/Tooltip';
import HoverTooltip from '@Components/HoverTooltip';
import ReactDOMServer from 'react-dom/server';
import RoutesCount from './RoutesCount';
import StatusFlagged from './StatusFlagged';
import RoutesLoaderInQueue from './RoutesLoaderInQueue';
import RoutesLoaderInProgress from './RoutesLoaderInProgress';

const renderStatus = ({
  status,
  label,
  type,
  routesCount,
  onSelectAlternativeTarget,
  openSynthesisJobRetryDialog,
  errorReason,
  queuePosition,
}) => {
  switch (status) {
    case ProjectStatus.FINISHED:
    case ProjectStatus.NEEDS_PROCESSING:
      return <RoutesCount type={type} value={routesCount} />;
    case ProjectStatus.USER_INTERACTION_NEEDED:
      return (
        <StatusFlagged
          type={type}
          status={status}
          tooltipContent={`Select alternative target for ${label}`}
          onClick={onSelectAlternativeTarget}
        />
      );
    case ProjectStatus.IN_PROGRESS:
      return <RoutesLoaderInProgress type={type} />;
    case ProjectStatus.IN_QUEUE:
      return <RoutesLoaderInQueue type={type} queuePosition={queuePosition} />;
    case ProjectStatus.NO_RESULTS:
      return <RoutesCount type={type} value={0} />;
    case ProjectStatus.ERROR_RETRY:
    case ProjectStatus.FATAL_ERROR:
      return (
        <StatusFlagged
          type={type}
          status={status}
          tooltipContent="Processing error: synthesis job needs to be resubmitted."
          onClick={() => openSynthesisJobRetryDialog(status, type)}
        />
      );
    case ProjectStatus.VALIDATION_ERROR:
      return <StatusFlagged type={type} status={status} tooltipContent={errorReason} label={null} />;
    case ProjectStatus.CLEANED:
      return (
        <StatusFlagged
          status={ProjectStatus.CLEANED}
          label="Project expired"
          tooltipContent="Routes have been removed from storage. Please rerun or delete the project."
        />
      );
    default:
      return null;
  }
};

function RoutesStatus({
  status,
  label,
  type,
  routesCount,
  onSelectAlternativeTarget,
  openSynthesisJobRetryDialog,
  errorReason,
  queuePosition,
  step,
  partner,
}) {
  let updatedLabel = label;
  const customChildStatus =
    type === 'predicted' && !!step && partner && (status === ProjectStatus.IN_PROGRESS || status === ProjectStatus.IN_QUEUE);
  if (customChildStatus) {
    updatedLabel =
      // eslint-disable-next-line no-nested-ternary
      step === 1
        ? 'Running project extension'
        : partner === ProjectSynthesisPartner.IKTOS
        ? 'Running first disconnect'
        : 'Running last step';
  }
  return (
    <div
      role="status"
      className={cn(
        'project-status-grid-item__routes-status',
        {
          'project-status-grid-item__routes-status--flag-label':
            status === ProjectStatus.USER_INTERACTION_NEEDED ||
            status === ProjectStatus.ERROR_RETRY ||
            status === ProjectStatus.FATAL_ERROR ||
            status === ProjectStatus.IN_PROGRESS ||
            status === ProjectStatus.IN_QUEUE,
        },
        { 'project-status-grid-item__routes-status--cleaned': status === ProjectStatus.CLEANED }
      )}>
      {customChildStatus && (
        <HoverTooltip
          id={TooltipConstants.ids.GlobalHover}
          className="project-status-grid-item__info-tooltip"
          tooltip={ReactDOMServer.renderToString(
            <>
              Default parameters in retrosynthesis search yielded <b>no results</b>. <br />
              We reran query with broader parameters for more results.
            </>
          )}
          place="left">
          <Button
            buttonConfig={{
              className: 'info-tooltip',
              type: 'link',
              size: 'lg',
              e2eName: 'project-status-grid-item__info',
              iconOnly: true,
              ariaLabel: 'Extended project information',
            }}
            iconConfig={{
              name: 'information-notitle',
              size: 'sm',
              noBorder: true,
              title: 'Extended project information',
            }}
          />
        </HoverTooltip>
      )}
      <span className="project-status-grid-item__label">{updatedLabel}</span>
      {renderStatus({
        status,
        label,
        type,
        routesCount,
        onSelectAlternativeTarget,
        openSynthesisJobRetryDialog,
        errorReason,
        queuePosition,
      })}
    </div>
  );
}

RoutesStatus.defaultProps = {
  onSelectAlternativeTarget: () => null,
  openSynthesisJobRetryDialog: () => null,
};

RoutesStatus.propTypes = {
  status: PropTypes.oneOf(ProjectStatus.allTypes),
  label: PropTypes.string,
  type: PropTypes.oneOf(ProjectSynthesisJobType.allTypes),
  routesCount: PropTypes.number,
  onSelectAlternativeTarget: PropTypes.func,
  openSynthesisJobRetryDialog: PropTypes.func,
  errorReason: PropTypes.string,
  queuePosition: PropTypes.string,
  step: PropTypes.number,
  partner: PropTypes.string,
};

export default RoutesStatus;
